import React, { useState } from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';
import { orDefault } from './authCopy';
import { FmBanner, FmBox, FmButton, FmEmpty, FmInline, FmStack, FmSteps, FmText, FmSelector, FmTextField, FmCheckbox, useFmForm, useFmToast } from '@/components/ui';
import { FmFormItem, FmFormShell } from '@/components/ui/internal/FmLegacyForm';
import { useAuthContext, AuthError } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n';
import type { FmFormHandle, FmSelectorItem } from '@/components/ui';

interface RegistrationData {
  email: string;
  password: string;
  confirmPassword: string;
  userType: 'owner' | 'professional' | 'buyer';
  firstName: string;
  lastName: string;
  organization?: string;
  regionsOfInterest: string[];
  interests: string[];
  terms: boolean;
  marketing: boolean;
  sentry_consent: boolean;
  invite_code?: string;
}

interface EnhancedRegisterFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

type Translations = ReturnType<typeof useLanguage>['t'];
type AuthCopy = Translations['auth'] | undefined;

const requireField = (value: unknown, fieldName: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  throw new Error(`Missing required registration field: ${fieldName}`);
};

function resolveEnhancedCopy(rawT: Translations | undefined) {
  const auth: Partial<NonNullable<AuthCopy>> = rawT?.auth ?? {};
  const v: Partial<NonNullable<NonNullable<AuthCopy>['validation']>> = auth.validation ?? {};
  const sentryConsent = auth.sentryConsent;
  if (typeof sentryConsent !== 'string' || sentryConsent.trim().length === 0) {
    throw new Error('Missing registration Sentry consent translation.');
  }
  const steps: Partial<NonNullable<NonNullable<AuthCopy>['steps']>> = auth.steps ?? {};
  const interests: Partial<NonNullable<NonNullable<AuthCopy>['interests']>> = auth.interests ?? {};
  const regions: Partial<NonNullable<Translations['regions']>> = rawT?.regions ?? {};
  return {
    stepAccount: orDefault(steps.account, 'Account'),
    stepAccountDesc: orDefault(steps.accountDesc, 'Create your login credentials'),
    stepProfile: orDefault(steps.profile, 'Profile'),
    stepProfileDesc: orDefault(steps.profileDesc, 'Tell us about yourself'),
    stepPreferences: orDefault(steps.preferences, 'Preferences'),
    stepPreferencesDesc: orDefault(steps.preferencesDesc, 'Customize your experience'),
    email: orDefault(auth.email, 'Email'),
    emailRequired: orDefault(v.emailRequired, 'Email is required'),
    emailInvalid: orDefault(v.emailInvalid, 'Invalid email format'),
    password: orDefault(auth.password, 'Password'),
    passwordRequired: orDefault(v.passwordRequired, 'Password is required'),
    passwordMin: orDefault(v.passwordMin, 'Password must be at least 8 characters'),
    passwordStrength: orDefault(v.passwordStrength, 'Password must contain uppercase, lowercase, and number'),
    confirmPassword: orDefault(auth.confirmPassword, 'Confirm Password'),
    confirmRequired: orDefault(v.confirmRequired, 'Please confirm password'),
    passwordMismatch: orDefault(v.passwordMismatch, 'Passwords do not match'),
    passwordRequirements: orDefault(auth.passwordRequirements, 'Password must contain:'),
    passwordReq1: orDefault(auth.passwordReq1, 'At least 8 characters'),
    passwordReq2: orDefault(auth.passwordReq2, 'One uppercase letter'),
    passwordReq3: orDefault(auth.passwordReq3, 'One lowercase letter'),
    passwordReq4: orDefault(auth.passwordReq4, 'One number'),
    userTypeRequired: orDefault(v.userTypeRequired, 'Please select user type'),
    userType: orDefault(auth.userType, 'User Type'),
    forestOwner: orDefault(auth.forestOwner, 'Forest Owner'),
    forestManager: orDefault(auth.forestManager, 'Forest Professional'),
    buyer: orDefault(auth.buyer, 'Buyer'),
    firstName: orDefault(auth.firstName, 'First Name'),
    firstNameRequired: orDefault(v.firstNameRequired, 'First name is required'),
    lastName: orDefault(auth.lastName, 'Last Name'),
    lastNameRequired: orDefault(v.lastNameRequired, 'Last name is required'),
    organization: orDefault(auth.organization, 'Organization (optional)'),
    selectRegion: orDefault(auth.selectRegion, 'Select regions of interest'),
    regionLapland: orDefault(regions.lapland, 'Lapland'),
    regionNorthern: orDefault(regions.northern, 'Northern Finland'),
    regionCentral: orDefault(regions.central, 'Central Finland'),
    regionEastern: orDefault(regions.eastern, 'Eastern Finland'),
    regionWestern: orDefault(regions.western, 'Western Finland'),
    regionSouthern: orDefault(regions.southern, 'Southern Finland'),
    interestValuation: orDefault(interests.valuation, 'Forest valuation'),
    interestPlanning: orDefault(interests.planning, 'Forest planning'),
    interestTrading: orDefault(interests.trading, 'Buying/selling forest'),
    interestManagement: orDefault(interests.management, 'Forest management'),
    interestSimulation: orDefault(interests.simulation, 'Growth simulations'),
    interestSustainability: orDefault(interests.sustainability, 'Sustainable forestry'),
    interests: orDefault(auth.selectInterests, 'Interests'),
    termsRequired: orDefault(v.termsRequired, 'You must accept the terms'),
    termsAgree: orDefault(auth.termsAgree, 'I agree to the Terms of Service and Privacy Policy'),
    sentryConsent,
    marketingConsent: orDefault(auth.marketingConsent, 'I want to receive newsletters and offers'),
    verificationPendingTitle: orDefault(auth.verificationPendingTitle, 'Registration successful — verify your email'),
    verificationPendingMessage: orDefault(auth.verificationPendingMessage, 'We sent a verification link to your email address. Please verify your email, then sign in.'),
    goToSignIn: orDefault(auth.goToSignIn, 'Go to sign in'),
    previous: orDefault(auth.previous, 'Previous'),
    next: orDefault(auth.next, 'Next'),
    completeRegistration: orDefault(auth.completeRegistration, 'Complete Registration'),
    cancel: orDefault(auth.cancel, 'Cancel'),
    registrationFailed: orDefault(auth.registrationFailed, 'Registration failed. Please try again.'),
    invitationCode: orDefault(auth.invitationCode, 'Invitation code'),
    invitationCodeRequired: orDefault(auth.invitationCodeRequired, 'Enter the invitation code you received.'),
    registrationClosed: orDefault(auth.registrationClosed, 'New account registration is currently closed.'),
    registrationCapabilityUnavailable: orDefault(auth.registrationCapabilityUnavailable, 'Registration availability could not be verified. Please try again later.'),
  };
}

type EnhancedCopy = ReturnType<typeof resolveEnhancedCopy>;

function resolveRegisterError(error: unknown, rawT: Translations | undefined, copy: EnhancedCopy): string {
  if (error instanceof AuthError) {
    const errors = rawT?.auth?.errors as Partial<Record<string, string>> | undefined;
    return orDefault(errors?.[error.code], copy.registrationFailed);
  }
  return copy.registrationFailed;
}

type RegisterFormHandle = FmFormHandle<RegistrationData>;

const renderAccountStep = (
  copy: EnhancedCopy,
  form: RegisterFormHandle,
  onSubmit: () => void,
  invitationRequired: boolean,
) => (
  <>
    <FmBox inset="md">
      <FmStack gap="xs">
        <FmText size="caption" weight="medium" tone="muted">{copy.passwordRequirements}:</FmText>
        <FmStack as="ul" gap="xs">
          <FmText as="li" size="fine" tone="muted">{copy.passwordReq1}</FmText>
          <FmText as="li" size="fine" tone="muted">{copy.passwordReq2}</FmText>
          <FmText as="li" size="fine" tone="muted">{copy.passwordReq3}</FmText>
          <FmText as="li" size="fine" tone="muted">{copy.passwordReq4}</FmText>
        </FmStack>
      </FmStack>
    </FmBox>
    <FmFormShell<RegistrationData> form={form} onSubmit={() => onSubmit()}>
      <FmFormItem name="email" label={copy.email} rules={[{ type: 'email', message: copy.emailInvalid }, { required: true, message: copy.emailRequired }]}>
        <FmTextField type="email" data-testid="register-email" />
      </FmFormItem>
      <FmFormItem name="password" label={copy.password} rules={[{ min: 8, message: copy.passwordMin }, { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, message: copy.passwordStrength }, { required: true, message: copy.passwordRequired }]}>
        <FmTextField type="password" data-testid="register-password" />
      </FmFormItem>
      <FmFormItem name="confirmPassword" label={copy.confirmPassword} rules={[{ required: true, message: copy.confirmRequired }]}>
        <FmTextField type="password" data-testid="register-confirm-password" />
      </FmFormItem>
      {invitationRequired ? (
        <FmFormItem
          name="invite_code"
          label={copy.invitationCode}
          rules={[{ required: true, message: copy.invitationCodeRequired }]}
        >
          <FmTextField data-testid="register-invite-code" />
        </FmFormItem>
      ) : null}
    </FmFormShell>
  </>
);

const renderProfileStep = (copy: EnhancedCopy, form: RegisterFormHandle, onSubmit: () => void) => {
  const userTypeOptions: FmSelectorItem[] = [
    { label: copy.forestOwner, value: 'owner' },
    { label: copy.forestManager, value: 'professional' },
    { label: copy.buyer, value: 'buyer' },
  ];

  return (
    <FmFormShell<RegistrationData> form={form} onSubmit={() => onSubmit()}>
      <FmBox inset="md">
        <FmStack gap="md">
          <FmFormItem name="userType" label={copy.userType} rules={[{ required: true, message: copy.userTypeRequired }]}>
            <FmSelector
              variant="radio"
              label={copy.userType}
              items={userTypeOptions}
              value={form.getValues().userType || ''}
              onChange={(value) => form.setValue('userType', value as RegistrationData['userType'])}
              data-testid="register-user-type"
            />
          </FmFormItem>

          <FmFormItem name="firstName" label={copy.firstName} rules={[{ required: true, message: copy.firstNameRequired }]}>
            <FmTextField data-testid="register-first-name" />
          </FmFormItem>

          <FmFormItem name="lastName" label={copy.lastName} rules={[{ required: true, message: copy.lastNameRequired }]}>
            <FmTextField data-testid="register-last-name" />
          </FmFormItem>

          <FmFormItem name="organization" label={copy.organization}>
            <FmTextField data-testid="register-organization" />
          </FmFormItem>
        </FmStack>
      </FmBox>
    </FmFormShell>
  );
};

const renderCheckboxGroup = (
  form: RegisterFormHandle,
  field: 'regionsOfInterest' | 'interests',
  heading: string,
  options: Record<string, string>,
) => {
  const current: string[] = form.getValues()[field] || [];
  return (
    <FmStack gap="sm">
      <FmText weight="medium">{heading}</FmText>
      <FmStack gap="sm">
        {Object.entries(options).map(([key, label]) => (
          <FmCheckbox
            key={key}
            checked={current.includes(key)}
            text={label}
            onChange={(checked) => {
              if (checked) {
                form.setValue(field, [...current, key]);
              } else {
                form.setValue(field, current.filter((v: string) => v !== key));
              }
            }}
          />
        ))}
      </FmStack>
    </FmStack>
  );
};

const renderPreferencesStep = (copy: EnhancedCopy, form: RegisterFormHandle, onSubmit: () => void) => (
  <FmFormShell<RegistrationData> form={form} onSubmit={() => onSubmit()}>
    <FmBox inset="md">
      <FmStack gap="md">
        {renderCheckboxGroup(form, 'regionsOfInterest', copy.selectRegion, {
          lapland: copy.regionLapland,
          northern: copy.regionNorthern,
          central: copy.regionCentral,
          eastern: copy.regionEastern,
          western: copy.regionWestern,
          southern: copy.regionSouthern,
        })}

        {renderCheckboxGroup(form, 'interests', copy.interests, {
          valuation: copy.interestValuation,
          planning: copy.interestPlanning,
          trading: copy.interestTrading,
          management: copy.interestManagement,
          simulation: copy.interestSimulation,
          sustainability: copy.interestSustainability,
        })}

        <FmFormItem name="terms" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error(copy.termsRequired)) }]}>
          <FmCheckbox data-testid="register-terms" labelSegments={[{ text: copy.termsAgree }]} />
        </FmFormItem>

        <FmFormItem name="sentry_consent" valuePropName="checked">
          <FmCheckbox data-testid="register-sentry-consent" text={copy.sentryConsent} />
        </FmFormItem>

        <FmFormItem name="marketing" valuePropName="checked">
          <FmCheckbox text={copy.marketingConsent} />
        </FmFormItem>
      </FmStack>
    </FmBox>
  </FmFormShell>
);

const VerificationPending: React.FC<{ copy: EnhancedCopy; onSuccess?: () => void }> = ({
  copy,
  onSuccess,
}) => (
  <FmBox inset="lg" data-testid="register-verification-pending">
    <FmEmpty
      icon={<CheckCircleOutlined />}
      description={
        <>
          <FmText weight="bold">{copy.verificationPendingTitle}</FmText>
          <FmText size="caption" tone="muted">{copy.verificationPendingMessage}</FmText>
        </>
      }
      action={
        <FmButton variant="primary" size="md" onClick={() => onSuccess?.()} data-testid="register-go-to-login">
          {copy.goToSignIn}
        </FmButton>
      }
    />
  </FmBox>
);

const registrationSteps = (copy: EnhancedCopy) => [
  { title: copy.stepAccount, description: copy.stepAccountDesc },
  { title: copy.stepProfile, description: copy.stepProfileDesc },
  { title: copy.stepPreferences, description: copy.stepPreferencesDesc },
];

export const EnhancedRegisterForm: React.FC<EnhancedRegisterFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const toast = useFmToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<RegistrationData>>({ sentry_consent: false });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [form] = useFmForm<RegistrationData>();
  const {
    register,
    registrationCapability: capability = null,
    registrationCapabilityError: capabilityError = true,
  } = useAuthContext();
  const { t } = useLanguage();

  const copy = resolveEnhancedCopy(t);

  const steps = registrationSteps(copy);

  const handleNext = async () => {
    try {
      const values = await form.validate() as Partial<RegistrationData>;

      if (currentStep === 0 && values.password !== values.confirmPassword) {
        form.setFieldError('confirmPassword', [copy.passwordMismatch]);
        return;
      }

      setFormData({ ...formData, ...values });
      setCurrentStep(currentStep + 1);
    } catch {
      // Validation error handled by form
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validate() as Partial<RegistrationData>;
      const completeData = { ...formData, ...values };

      if (!completeData.terms) {
        form.setFieldError('terms', [copy.termsRequired]);
        return;
      }

      setLoading(true);
      const registrationData = {
        username: requireField(completeData.email, 'email'),
        email: requireField(completeData.email, 'email'),
        password: requireField(completeData.password, 'password'),
        first_name: requireField(completeData.firstName, 'firstName'),
        last_name: requireField(completeData.lastName, 'lastName'),
        organization: completeData.organization,
        sentry_consent: completeData.sentry_consent === true,
        invite_code: completeData.invite_code?.trim() || undefined,
      };

      await register(registrationData);
      setRegistered(true);
    } catch (error: unknown) {
      toast.error(resolveRegisterError(error, t, copy));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderAccountStep(
          copy,
          form,
          () => void handleNext(),
          capability?.invitation_required === true,
        );
      case 1:
        return renderProfileStep(copy, form, () => void handleNext());
      case 2:
        return renderPreferencesStep(copy, form, () => void handleSubmit());
      default:
        return null;
    }
  };

  if (registered) {
    return <VerificationPending copy={copy} onSuccess={onSuccess} />;
  }

  const registrationBlocked =
    capabilityError || capability?.registration_available === false;

  return (
    <FmBox inset="lg">
      <FmStack gap="lg">
        {registrationBlocked ? (
          <FmBanner
            tone="error"
            data-testid="registration-capability-blocked"
            title={
              capabilityError
                ? copy.registrationCapabilityUnavailable
                : copy.registrationClosed
            }
          />
        ) : null}
        <FmSteps
          aria-label="Registration progress"
          current={currentStep}
          items={steps.map((item) => ({
            title: item.title,
            description: item.description,
          }))}
        />

        {renderStepContent()}

        <FmInline block justify="between" gap="md">
          {currentStep > 0 && (
            <FmButton variant="default" size="md" onClick={handlePrevious} data-testid="register-prev-step">
              {copy.previous}
            </FmButton>
          )}
          {currentStep < steps.length - 1 && (
            <FmButton
              variant="primary"
              size="md"
              onClick={handleNext}
              disabled={registrationBlocked}
              data-testid="register-next-step"
            >
              {copy.next}
            </FmButton>
          )}
          {currentStep === steps.length - 1 && (
            <FmButton
              variant="primary"
              size="md"
              loading={loading}
              disabled={registrationBlocked || capability === null}
              onClick={handleSubmit}
              data-testid="register-complete"
            >
              {copy.completeRegistration}
            </FmButton>
          )}
          {onCancel && (
            <FmButton
              variant="text"
              size="md"
              onClick={onCancel}
              data-testid="register-switch-login"
            >
              {copy.goToSignIn}
            </FmButton>
          )}
        </FmInline>
      </FmStack>
    </FmBox>
  );
};

export default EnhancedRegisterForm;
